import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Avatar } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import RightSidebar from '../../style-components/RightSidebar.jsx';
import AIChat from '../MediBot/AIChat.jsx';

import NavBar from './NavBar.jsx';
import Loading from '../../style-components/Loading.jsx';
import { getCurrentUser } from '../../models/user';
import { sideMenuTeacher, sideMenuAdmin } from '../constants/constant';
import { signOut } from '../../utils/cognitoAuth';
import backgroundImage from '../../assets/images/IMG_BACKGROUND_LOGIN.png';
import logoHorizontal from '../../assets/images/Logo_letters_white.png';
import arrow from '../../assets/images/ICON_back_arrow.svg';
import { useMediBot } from '../../models/MediBot';

function Header({ children }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { role } = useMediBot();

  const [showNavBar, setShowNavBar] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [chatPosition, setChatPosition] = useState(() => {
    const saved = localStorage.getItem('chatPosition');
    return saved ? JSON.parse(saved) : { right: '40px', bottom: '20px' };
  });

  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startRight = parseInt(chatPosition.right);
    const startBottom = parseInt(chatPosition.bottom);
    let hasMoved = false;

    const onMouseMove = (ev) => {
      const deltaX = ev.clientX - startX;
      const deltaY = ev.clientY - startY;

      // Only start dragging if mouse has moved more than 5 pixels
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        setIsDragging(true);
        hasMoved = true;
      }

      if (hasMoved) {
        const newRight = Math.max(10, Math.min(window.innerWidth - 60, startRight - deltaX));
        const newBottom = Math.max(10, Math.min(window.innerHeight - 60, startBottom - deltaY));

        setChatPosition({
          right: `${newRight}px`,
          bottom: `${newBottom}px`,
        });
      }
    }

    const onMouseUp = () => {
      if (!hasMoved) {
        // Only open chat if it was a click, not a drag
        setChatOpen(true);
      }

      if (hasMoved) {
        // Save position to localStorage only if it was dragged
        const newPosition = {
          right: chatPosition.right,
          bottom: chatPosition.bottom,
        };
        localStorage.setItem('chatPosition', JSON.stringify(newPosition));
      }

      setIsDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const styles = {
    backgroundImage: {
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      width: '100%',
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      paddingLeft: '30px',
    },
    navbarContainer: {
      display: 'flex',
      flexDirection: 'row',
      maxWidth: '100%',
    },
    name: {
      color: theme.palette.white.main,
    },
    arrow: {
      position: 'fixed',
      bottom: '20px',
      left: showNavBar ? '40px' : '10px',
      transform: showNavBar ? 'rotate(0deg)' : 'rotate(180deg)',
    },
    image: {
      width: '260px',
    },
    chat: {
      position: 'fixed',
      bottom: chatPosition.bottom,
      right: chatPosition.right,
      cursor: isDragging ? 'grabbing' : 'grab',
      border: isDragging ? '2px solid #87CEEB' : 'none',
      borderRadius: '50%',
      padding: '4px',
      backgroundColor: isDragging ? 'rgba(135, 206, 235, 0.1)' : 'transparent',
      transition: isDragging ? 'none' : 'all 0.3s ease',
      zIndex: 1000,
      opacity: chatOpen ? 0 : 1,
      transform: chatOpen ? 'scale(0.8)' : 'scale(1)',
      pointerEvents: chatOpen ? 'none' : 'auto',
    },
    avatar: {
      backgroundColor: theme.palette.primary.main,
      width: 56,
      height: 56,
    },
  };

  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState([]);
  const [userName, setUserName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setLoading(true);
    getCurrentUser()
      .then(user => {
        switch (user?.role) {
          case 'TEACHER':
            setUserName(`${user?.firstnames} ${user?.lastnames}`);
            setMenu(sideMenuTeacher);
            setLoading(false);
            break;
          case 'ADMIN':
            setUserName(`${user?.firstnames} ${user?.lastnames}`);
            setMenu(sideMenuAdmin);
            setLoading(false);
            break;
          default:
            setLoading(false);
            break;
        }
      })
      .catch(() => setLoading(false));
  }, []);

  const handleClick = () => {
    setShowNavBar(!showNavBar);
  };

  const handleLogo = () => {
    navigate('/index');
  };

  const closeChat = useCallback(() => {
    setChatOpen(false);
    setTimeout(() => {
      try {
        navigate(0);
      } catch (error) {
        window.location.reload();
      }
    }, 300);
  }, [navigate]);

  return (
    <div>
      <div style={styles.backgroundImage}>
        <div style={styles.header}>
          <img src={logoHorizontal} alt='Logo' style={styles.image} onClick={handleLogo} />
          <div>
            {userName ? (
              <Typography variant='h7' style={styles.name}>
                Bienvenido/a <b>{userName}</b>
              </Typography>
            ) : null}
            <IconButton
              id='basic-button'
              color='white'
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
              onClick={event => setAnchorEl(event.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              id='basic-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(false)}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate('/profile');
                }}
                color={theme.palette.primary.main}>
                Mi perfil
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  signOut();
                  navigate('/');
                }}
                color={theme.palette.primary.main}>
                Cerrar sesión
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
      {loading && <Loading />}
      {showNavBar && !loading && (
        <div style={styles.navbarContainer}>
          <NavBar menu={menu} />
          <div className='relative w-full mx-auto items-center'>
            <Outlet />
            {children}
          </div>
        </div>
      )}
      {!showNavBar && !loading && (
        <>
          <Outlet />
          {children}
        </>
      )}
      {userName ? <img src={arrow} alt='Arrow' style={styles.arrow} onClick={handleClick} /> : null}

      {role && (
        <div
          style={styles.chat}
          onMouseDown={onMouseDown}
        >
          <Avatar style={styles.avatar}>
            <SmartToyOutlinedIcon style={{ width: '75%', height: '75%' }} />
          </Avatar>
        </div>
      )}

      {/* Right Sidebar */}
      <RightSidebar open={chatOpen} onClose={closeChat} title={"Chat IA"}>
        <AIChat onClose={closeChat} open={chatOpen} />
      </RightSidebar>
    </div>
  );
}

export default Header;
